import { Button } from '@pets-ui/Button/Button'
import { HStack } from '@pets-ui/HStack/HStack'
import { ArrowLeftSmall } from '@pets-ui/icons'
import Cookies from 'js-cookie'
import { type CountryCode, isValidPhoneNumber, parsePhoneNumber } from 'libphonenumber-js'
import { type Dispatch, type SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { useRequestAccountBindingStatus } from '../../../hooks/apis/wp/useRequestAccountBindingStatus'
import { type Country } from '../../../types/dataModel/platformSdk'
import { wordPressNavigate } from '../../../utils/wordPressRoute'
import { CardTitleWithIcon } from '../login/CardTitleWithIcon'
import { InputInternationalPhoneNumber } from '../login/InputInternationalPhoneNumber'
import { BindStep } from '../login/types'
import { useContextMyAccountDialog } from './Provider/useContextMyAccountDialog'

interface BindPhoneProps {
  phone: string
  setPhone: Dispatch<SetStateAction<string>>
  phoneError: string
  setPhoneError: Dispatch<SetStateAction<string>>
  setStep: (step: BindStep) => void
  countryCode?: Country['countryIsoCode']
  setCountryCode: Dispatch<SetStateAction<Country['countryIsoCode'] | undefined>>
}

export function BindPhone(props: BindPhoneProps) {
  const { t } = useTranslation()
  const { setStep, phone, phoneError, setCountryCode, setPhone, setPhoneError, countryCode } = props
  const { showDialogServerError } = useContextMyAccountDialog()
  const { trigger, isMutating } = useRequestAccountBindingStatus()

  const validatePhone = async () => {
    if (!countryCode) return
    if (phone.trim() === '') {
      setPhoneError(String(t('login.errorPleaseEnterPhone')))
      return
    }

    if (!isValidPhoneNumber(phone, countryCode as CountryCode)) {
      setPhoneError(String(t('error.incorrectPhoneNumberFormat')))
      return
    }
    try {
      const { data } = await trigger({ account: parsePhoneNumber(phone, countryCode as CountryCode).number, type: 'sms' })
      if (data.data.isBound) {
        setPhoneError(String(t('login.phoneIsAlreadyBound')))
        return
      }
      setPhoneError('')
      Cookies.set('account', parsePhoneNumber(phone, countryCode as CountryCode).number)
      Cookies.set('country_code', countryCode)
      setStep(BindStep.VALIDATE)
    }
    catch (e) {
      console.error(e)
      showDialogServerError()
    }
  }

  const handleGoBack = () => {
    const referrer = document.referrer
    const currentOrigin = window.location.origin
    const defaultRedirectUrl = '/my-account/edit-account/'

    if (!referrer) {
      wordPressNavigate(defaultRedirectUrl)
      return
    }

    try {
      const referrerUrl = new URL(referrer)
      if (referrerUrl.origin !== currentOrigin) {
        wordPressNavigate(defaultRedirectUrl)
        return
      }

      window.history.back()
    }
    catch (error) {
      console.warn('Failed to parse referrer URL:', error)
      wordPressNavigate(defaultRedirectUrl)
    }
  }

  return (
    <>
      <CardTitleWithIcon
        title={t('login.verifyIdentity')}
        leftIcon={<ArrowLeftSmall />}
        onClickLeft={handleGoBack}
      />
      <HStack gap={5}>
        <InputInternationalPhoneNumber
          label={t('login.phoneNumber')}
          phone={phone}
          setPhone={setPhone}
          phoneError={phoneError}
          validatePhone={validatePhone}
          countryCode={countryCode}
          setCountryCode={setCountryCode}
        />
        <Button
          loading={isMutating}
          fullWidth
          onClick={validatePhone}
          disabled={phone.trim() === ''}
        >
          {t('button.confirm')}
        </Button>
      </HStack>
    </>
  )
}
